import React, { useState, useEffect } from 'react';
import API from '../../utils/api';
import './CouponsView.css';

function CouponsView() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingCouponId, setEditingCouponId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const role = localStorage.getItem('role');

  const [formData, setFormData] = useState({
    code: '',
    discountType: 'Percentage',
    discountValue: '',
    validUntil: '',
    maxUses: 0,
    isActive: true
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/admin/coupons');
      setCoupons(res.data);
    } catch (err) {
      console.error("Error fetching coupons:", err);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      discountValue: Number(formData.discountValue),
      maxUses: Number(formData.maxUses)
    };

    try {
      if (editMode) {
        await API.put(`/api/admin/coupons/${editingCouponId}`, payload);
        alert('Coupon updated successfully');
      } else {
        await API.post('/api/admin/coupons', payload);
        alert('Coupon added successfully');
      }
      setShowModal(false);
      fetchCoupons();
    } catch (err) {
      alert(err.response?.data?.message || 'Error saving coupon');
    }
  };

  const handleEditClick = (coupon) => {
    setEditMode(true);
    setEditingCouponId(coupon._id);
    setFormData({
      code: coupon.code,
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      validUntil: new Date(coupon.validUntil).toISOString().split('T')[0],
      maxUses: coupon.maxUses,
      isActive: coupon.isActive
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      try {
        await API.delete(`/api/admin/coupons/${id}`);
        fetchCoupons();
      } catch (err) {
        alert('Failed to delete coupon');
      }
    }
  };

  const filteredCoupons = coupons.filter(c =>
    c.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-view">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
        <h2>Manage Coupons</h2>
        
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input 
            type="text" 
            placeholder="Search coupons..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            style={{ padding: '10px 15px', borderRadius: '8px', border: '1px solid #ccc', minWidth: '200px' }}
          />

          {role === 'Super Admin' && (
            <button 
              className="add-btn" 
              onClick={() => {
                setEditMode(false);
                setEditingCouponId(null);
                setFormData({
                  code: '',
                  discountType: 'Percentage',
                  discountValue: '',
                  validUntil: '',
                  maxUses: 0,
                  isActive: true
                });
                setShowModal(true);
              }}
            >
              + Create Coupon
            </button>
          )}
        </div>
      </div>

      <div className="table-container">
        {loading ? (
          <p style={{ textAlign: 'center', padding: '20px' }}>Loading coupons...</p>
        ) : coupons.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            <p>No coupons found. Create one to get started.</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Valid Until</th>
                <th>Usage</th>
                <th>Status</th>
                {role === 'Super Admin' && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredCoupons.map(coupon => (
                <tr key={coupon._id}>
                  <td style={{ fontWeight: 'bold', color: '#0f172a' }}>{coupon.code}</td>
                  <td style={{ color: '#0ea5e9', fontWeight: 'bold' }}>
                    {coupon.discountType === 'Percentage' ? `${coupon.discountValue}%` : `₹${coupon.discountValue}`}
                  </td>
                  <td>{new Date(coupon.validUntil).toLocaleDateString()}</td>
                  <td>
                    {coupon.usedCount} / {coupon.maxUses === 0 ? 'Unlimited' : coupon.maxUses}
                  </td>
                  <td>
                    <span className={coupon.isActive && new Date(coupon.validUntil) > new Date() ? 'status-active' : 'status-inactive'}>
                      {coupon.isActive && new Date(coupon.validUntil) > new Date() ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {role === 'Super Admin' && (
                    <td>
                      <button className="action-btn edit-btn" onClick={() => handleEditClick(coupon)}>Edit</button>
                      <button className="action-btn delete-btn" onClick={() => handleDelete(coupon._id)}>Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content small-modal">
            <span className="close-btn" onClick={() => setShowModal(false)}>&times;</span>
            <h3 style={{ marginBottom: '20px', color: '#0284c7' }}>{editMode ? 'Edit Coupon' : 'Create New Coupon'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="input-group">
                <label>Coupon Code *</label>
                <input 
                  type="text" 
                  name="code" 
                  required 
                  value={formData.code} 
                  onChange={handleInputChange} 
                  placeholder="e.g. DIWALI50"
                  style={{ textTransform: 'uppercase' }}
                />
              </div>

              <div className="input-group" style={{ marginTop: '15px' }}>
                <label>Discount Type *</label>
                <select name="discountType" value={formData.discountType} onChange={handleInputChange}>
                  <option value="Percentage">Percentage (%)</option>
                  <option value="Fixed Amount">Fixed Amount (₹)</option>
                </select>
              </div>

              <div className="input-group" style={{ marginTop: '15px' }}>
                <label>Discount Value *</label>
                <input 
                  type="number" 
                  name="discountValue" 
                  required 
                  value={formData.discountValue} 
                  onChange={handleInputChange} 
                  placeholder={formData.discountType === 'Percentage' ? 'e.g. 10' : 'e.g. 5000'}
                />
              </div>

              <div className="input-group" style={{ marginTop: '15px' }}>
                <label>Valid Until *</label>
                <input 
                  type="date" 
                  name="validUntil" 
                  required 
                  value={formData.validUntil} 
                  onChange={handleInputChange} 
                />
              </div>

              <div className="input-group" style={{ marginTop: '15px' }}>
                <label>Max Uses (0 for unlimited)</label>
                <input 
                  type="number" 
                  name="maxUses" 
                  value={formData.maxUses} 
                  onChange={handleInputChange} 
                />
              </div>

              <div className="input-group" style={{ marginTop: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input 
                  type="checkbox" 
                  name="isActive" 
                  checked={formData.isActive} 
                  onChange={handleInputChange} 
                  id="isActiveCoupon"
                  style={{ width: 'auto' }}
                />
                <label htmlFor="isActiveCoupon" style={{ margin: 0 }}>Active</label>
              </div>

              <div className="submit-area" style={{ marginTop: '20px' }}>
                <button type="submit" className="submit-btn">{editMode ? 'Update Coupon' : 'Create Coupon'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CouponsView;
